import { Injectable, inject } from '@angular/core';
import { formatDate } from '@angular/common';
import { jsPDF } from 'jspdf';
import { PdfFormFontService } from './pdf-form-font.service';
import {
  PrintTranslateFn,
  PrintTranslationService
} from './print-translation.service';
import { OrganizationUnit } from '../models/OrganizationUnit';
import { Setting } from '../models/Setting';
import {
  cleanPrintText,
  signerSvNumber,
  signerTitleText
} from '../utils/print-signer.util';
import {
  openPdfPreviewWindow,
  showPdfPreview
} from '../utils/pdf-preview.util';

export interface VisitCertificatePrintData {
  /** Language of the printout (fi/sv/en/pl): the fixed texts follow it,
   *  stored data prints as is. */
  language: string;
  customerName: string;
  visitDate: Date | string | null;
  /** "HH:mm" */
  startTime: string | null;
  endTime: string | null;
  staffName: string;
  /** The staff member's speciality codes (User.speciality). */
  specialityCodes: Array<string>;
  /** SPECIALITIES settings in the printout language. */
  specialitySettings: Array<Setting>;
  /** SPECIALITIES settings in the UI language, for codes without a label in
   *  the printout language. */
  fallbackSpecialitySettings?: Array<Setting>;
  /** The printing user. */
  signerName: string;
  /** User.signature — "Lääkärin erikoisala(t) (Kela-lomakkeisiin)". */
  signerSignature?: string | null;
  /** User.registryNumber — the SV number. */
  signerRegistryNumber?: string | null;
  /** The clinic of the header and the footer. */
  organizationUnit: OrganizationUnit | null;
  printDate?: Date;
}

const I18N = 'event.visit-certificate-print';

const MM = 72 / 25.4;
type Rgb = [number, number, number];
// Layout plan colours: black text, #333 for the field labels, the signature
// captions and the print date, #9A9A9A row rules. The top rule and the
// signature lines are black.
const BLACK: Rgb = [0, 0, 0];
const DARK_GRAY: Rgb = [51, 51, 51];
const RULE_GRAY: Rgb = [154, 154, 154];
/** 1 px at 96 dpi, like the layout plan's lines. */
const RULE_WIDTH = 0.75;

const VALUE_SIZE = 11;
const VALUE_LINE_HEIGHT = 1.35;
const LABEL_SIZE = 9.5;
const LABEL_LINE_HEIGHT = 1.35;
const ROW_MIN_HEIGHT = 35;
const ROW_PADDING_Y = 10;
/** Width of the label column — the value column starts after it. */
const LABEL_COLUMN_WIDTH = 135;
const SIGNATURE_COLUMN_OFFSET = 159;
const SIGNER_SIZE = 10.5;
const SIGNER_LINE_STEP = 17;
const FOOTER_SIZE = 10;
/** Gap between the details list and the signature lines (layout plan), and
 *  how far it may narrow before the signer's title rows are shortened. */
const SIGNATURE_GAP = 85;
const SIGNATURE_GAP_MIN = 40;
const FOOTER_SEPARATOR = ' \u00B7 ';

interface FooterPart {
  text: string;
  bold: boolean;
}

interface ClinicFooterLayout {
  lines: Array<Array<FooterPart>>;
  firstBaseline: number;
  lineStep: number;
  ruleY: number;
}

/**
 * Käyntitodistus (AL-2893): an A4 certificate of the customer's visit that
 * works as the clinic's business card — clinic name and print date at the
 * top, the title with a short certificate text, the visit details as a
 * two-column list, the date and signature lines with the printing user's
 * name, Kela speciality text and SV number, and the clinic contact line at
 * the bottom (no www).
 *
 * A PDF instead of window.print(): no browser header/footer (date, URL,
 * page number) on the paper, and the one-page A4 layout stays the same in
 * every browser. jsPDF with the bundled Noto Sans, like the Eränumerokortti
 * printout (MaterialEntryPdfService).
 */
@Injectable({
  providedIn: 'root'
})
export class VisitCertificatePdfService {
  private readonly pdfFormFont = inject(PdfFormFontService);
  private readonly printTranslation = inject(PrintTranslationService);

  async buildCertificatePdf(data: VisitCertificatePrintData): Promise<Blob> {
    const translate = await this.printTranslation.getTranslator(data.language);
    const printDate = data.printDate || new Date();
    const title = translate(`${I18N}.title`);

    const doc = this.createDocument();
    await this.pdfFormFont.applyUnicodeFont(doc);
    doc.setProperties({ title });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 20 * MM;
    const contentWidth = pageWidth - marginX * 2;
    const rightX = marginX + contentWidth;

    /* ── Header: clinic name left, print date right ───────────────── */
    let cursorY = 63;
    const printedText = translate(`${I18N}.printed`, {
      date: formatDate(printDate, 'd.M.yyyy', 'fi-FI')
    });
    this.setFont(doc, 'normal', 10, DARK_GRAY);
    const printedWidth = doc.getTextWidth(printedText);
    doc.text(printedText, rightX, cursorY, { align: 'right' });

    const clinicName = cleanPrintText(data.organizationUnit?.name);
    if (clinicName) {
      this.setFont(doc, 'bold', 12, BLACK);
      const nameLines = this.splitLines(
        doc,
        clinicName,
        contentWidth - printedWidth - 24,
        2
      );
      doc.text(nameLines, marginX, cursorY, { lineHeightFactor: 1.25 });
      cursorY += (nameLines.length - 1) * 12 * 1.25;
    }

    /* ── Title and the certificate text ──────────────────────────── */
    cursorY += 67;
    this.setFont(doc, 'bold', 22, BLACK);
    const titleLines = this.splitLines(doc, title, contentWidth, 2);
    doc.text(titleLines, marginX, cursorY, { lineHeightFactor: 1.15 });
    cursorY += (titleLines.length - 1) * 22 * 1.15;

    cursorY += 26;
    this.setFont(doc, 'normal', VALUE_SIZE, BLACK);
    const statementLines = this.splitLines(
      doc,
      translate(`${I18N}.statement`),
      contentWidth,
      3
    );
    doc.text(statementLines, marginX, cursorY, { lineHeightFactor: 1.4 });
    cursorY += (statementLines.length - 1) * VALUE_SIZE * 1.4;

    /* ── Visit details: label | value rows under a black top rule ── */
    cursorY += 34;
    this.drawRule(doc, marginX, rightX, cursorY, BLACK, RULE_WIDTH);

    const rows = [
      { label: `${I18N}.customer`, value: data.customerName },
      { label: `${I18N}.date`, value: this.formatDay(data.visitDate) },
      {
        label: `${I18N}.time`,
        value: [cleanPrintText(data.startTime), cleanPrintText(data.endTime)]
          .filter((part) => part.length > 0)
          .join('\u2013')
      },
      { label: `${I18N}.reason-label`, value: translate(`${I18N}.reason`) },
      { label: `${I18N}.staff`, value: data.staffName },
      {
        label: `${I18N}.specialities`,
        value: this.specialityLabels(data, translate).join(', ')
      }
    ]
      .map((row) => ({
        label: translate(row.label),
        value: cleanPrintText(row.value)
      }))
      // An empty value leaves its row out — no hollow rows on the paper.
      .filter((row) => row.value.length > 0);

    const valueX = marginX + LABEL_COLUMN_WIDTH;
    const valueWidth = contentWidth - LABEL_COLUMN_WIDTH;
    const valueLineHeight = VALUE_SIZE * VALUE_LINE_HEIGHT;
    const labelLineHeight = LABEL_SIZE * LABEL_LINE_HEIGHT;
    for (const row of rows) {
      this.setFont(doc, 'normal', VALUE_SIZE, BLACK);
      const valueLines = this.splitLines(doc, row.value, valueWidth, 2);
      this.setFont(doc, 'bold', LABEL_SIZE, DARK_GRAY);
      const labelLines = this.splitLines(
        doc,
        row.label,
        LABEL_COLUMN_WIDTH - 12,
        2
      );
      const blockHeight = Math.max(
        valueLines.length * valueLineHeight,
        labelLines.length * labelLineHeight
      );
      const rowHeight = Math.max(
        ROW_MIN_HEIGHT,
        blockHeight + ROW_PADDING_Y * 2
      );
      // One-line rows center their text; the label sits on the baseline of
      // the value's first line.
      const baseline =
        cursorY +
        (rowHeight - blockHeight) / 2 +
        valueLineHeight / 2 +
        VALUE_SIZE * 0.35;
      doc.text(labelLines, marginX, baseline, {
        lineHeightFactor: LABEL_LINE_HEIGHT
      });
      this.setFont(doc, 'normal', VALUE_SIZE, BLACK);
      doc.text(valueLines, valueX, baseline, {
        lineHeightFactor: VALUE_LINE_HEIGHT
      });
      cursorY += rowHeight;
      this.drawRule(doc, marginX, rightX, cursorY, RULE_GRAY, RULE_WIDTH);
    }

    /* ── Footer first: the signature block has to end above it ───── */
    const footer = this.layoutClinicFooter(
      doc,
      data.organizationUnit,
      translate,
      contentWidth,
      pageHeight - 15 * MM
    );
    const contentLimit = (footer ? footer.ruleY : pageHeight - 15 * MM) - 14;

    /* ── Signature: date and signature lines, the signer below ───── */
    const signatureX = marginX + SIGNATURE_COLUMN_OFFSET;
    const signatureWidth = rightX - signatureX;
    // Name, title row ("LL, naistentautien ja synnytysten erikoislääkäri")
    // and the SV number on its own row; a row whose data is not stored is
    // left out. Measured first so the block can be placed above the footer.
    const signerName = cleanPrintText(data.signerName);
    const signerTitle = signerTitleText(data.signerSignature);
    const svNumber = signerSvNumber(data.signerRegistryNumber);
    const buildSignerRows = (
      titleMaxLines: number
    ): Array<{
      lines: Array<string>;
      style: 'normal' | 'bold';
      size: number;
    }> => {
      const rows: Array<{
        lines: Array<string>;
        style: 'normal' | 'bold';
        size: number;
      }> = [];
      if (signerName) {
        this.setFont(doc, 'bold', VALUE_SIZE, BLACK);
        rows.push({
          lines: this.splitLines(doc, signerName, signatureWidth, 2),
          style: 'bold',
          size: VALUE_SIZE
        });
      }
      this.setFont(doc, 'normal', SIGNER_SIZE, BLACK);
      if (signerTitle) {
        rows.push({
          lines: this.splitLines(
            doc,
            signerTitle,
            signatureWidth,
            titleMaxLines
          ),
          style: 'normal',
          size: SIGNER_SIZE
        });
      }
      if (svNumber) {
        rows.push({
          lines: this.splitLines(
            doc,
            translate(`${I18N}.sv-number`, { number: svNumber }),
            signatureWidth,
            1
          ),
          style: 'normal',
          size: SIGNER_SIZE
        });
      }
      return rows;
    };
    // Height from the signature line down to below the last signer row.
    const heightBelowLine = (
      rows: Array<{ lines: Array<string>; size: number }>
    ): number =>
      rows.length === 0
        ? 13 + 4
        : 46 +
          rows.reduce(
            (sum, row) => sum + (row.lines.length - 1) * row.size * 1.4,
            0
          ) +
          (rows.length - 1) * SIGNER_LINE_STEP +
          4;
    let signerRows = buildSignerRows(4);
    for (
      let titleMaxLines = 3;
      titleMaxLines >= 1 &&
      cursorY + SIGNATURE_GAP_MIN + heightBelowLine(signerRows) > contentLimit;
      titleMaxLines--
    ) {
      signerRows = buildSignerRows(titleMaxLines);
    }
    // The layout plan's gap above the signature lines, narrowed when long
    // values would otherwise push the signer onto the footer.
    const signatureLineY =
      cursorY +
      Math.max(
        SIGNATURE_GAP_MIN,
        Math.min(
          SIGNATURE_GAP,
          contentLimit - heightBelowLine(signerRows) - cursorY
        )
      );

    this.setFont(doc, 'normal', VALUE_SIZE, BLACK);
    doc.text(
      formatDate(printDate, 'dd.MM.yyyy', 'fi-FI'),
      marginX,
      signatureLineY - 8
    );
    this.drawRule(
      doc,
      marginX,
      marginX + LABEL_COLUMN_WIDTH,
      signatureLineY,
      BLACK,
      RULE_WIDTH
    );
    this.drawRule(doc, signatureX, rightX, signatureLineY, BLACK, RULE_WIDTH);

    this.setFont(doc, 'bold', LABEL_SIZE, DARK_GRAY);
    doc.text(translate(`${I18N}.date`), marginX, signatureLineY + 13);
    doc.text(translate(`${I18N}.signature`), signatureX, signatureLineY + 13);

    let signerY = signatureLineY + 46;
    for (const row of signerRows) {
      this.setFont(doc, row.style, row.size, BLACK);
      doc.text(row.lines, signatureX, signerY, { lineHeightFactor: 1.4 });
      signerY += (row.lines.length - 1) * row.size * 1.4 + SIGNER_LINE_STEP;
    }

    if (footer) {
      this.drawClinicFooter(doc, footer, marginX, contentWidth);
    }

    return doc.output('blob');
  }

  /**
   * The clinic on one centered line above the bottom margin: name in bold,
   * then address, phone and email (no www on this printout). When the line
   * does not fit, the parts that no longer fit move to the next line; a
   * single part wider than the page shrinks instead of running over the edge.
   * Laid out before the content so the content can stay above it.
   */
  private layoutClinicFooter(
    doc: jsPDF,
    unit: OrganizationUnit | null,
    translate: PrintTranslateFn,
    contentWidth: number,
    lastBaseline: number
  ): ClinicFooterLayout | null {
    const contactInfo = unit?.contactInfo;
    const address = [
      cleanPrintText(contactInfo?.address),
      [cleanPrintText(contactInfo?.postCode), cleanPrintText(contactInfo?.city)]
        .filter((part) => part.length > 0)
        .join(' ')
    ]
      .filter((part) => part.length > 0)
      .join(', ');
    const phone = cleanPrintText(contactInfo?.phone);
    const parts: Array<FooterPart> = [
      { text: cleanPrintText(unit?.name), bold: true },
      { text: address, bold: false },
      {
        text: phone ? translate(`${I18N}.phone`, { phone }) : '',
        bold: false
      },
      { text: cleanPrintText(contactInfo?.email), bold: false }
    ].filter((part) => part.text.length > 0);
    if (!parts.length) {
      return null;
    }

    const lines: Array<Array<FooterPart>> = [[]];
    for (const part of parts) {
      const current = lines[lines.length - 1];
      if (
        current.length > 0 &&
        this.footerLineWidth(doc, [...current, part], FOOTER_SIZE) >
          contentWidth
      ) {
        lines.push([part]);
      } else {
        current.push(part);
      }
    }
    const lineStep = FOOTER_SIZE * 1.4;
    const firstBaseline = lastBaseline - (lines.length - 1) * lineStep;
    return { lines, firstBaseline, lineStep, ruleY: firstBaseline - 17 };
  }

  private drawClinicFooter(
    doc: jsPDF,
    footer: ClinicFooterLayout,
    marginX: number,
    contentWidth: number
  ): void {
    this.drawRule(
      doc,
      marginX,
      marginX + contentWidth,
      footer.ruleY,
      RULE_GRAY,
      RULE_WIDTH
    );
    footer.lines.forEach((line, index) => {
      const naturalWidth = this.footerLineWidth(doc, line, FOOTER_SIZE);
      const size =
        naturalWidth > contentWidth
          ? Math.max(6, (FOOTER_SIZE * contentWidth) / naturalWidth)
          : FOOTER_SIZE;
      let x =
        marginX +
        Math.max(0, (contentWidth - this.footerLineWidth(doc, line, size)) / 2);
      const y = footer.firstBaseline + index * footer.lineStep;
      line.forEach((part, partIndex) => {
        if (partIndex > 0) {
          this.setFont(doc, 'normal', size, BLACK);
          doc.text(FOOTER_SEPARATOR, x, y);
          x += doc.getTextWidth(FOOTER_SEPARATOR);
        }
        this.setFont(doc, part.bold ? 'bold' : 'normal', size, BLACK);
        doc.text(part.text, x, y);
        x += doc.getTextWidth(part.text);
      });
    });
  }

  private footerLineWidth(
    doc: jsPDF,
    line: Array<FooterPart>,
    size: number
  ): number {
    return line.reduce((sum, part, index) => {
      this.setFont(doc, part.bold ? 'bold' : 'normal', size, BLACK);
      let width = sum + doc.getTextWidth(part.text);
      if (index > 0) {
        this.setFont(doc, 'normal', size, BLACK);
        width += doc.getTextWidth(FOOTER_SEPARATOR);
      }
      return width;
    }, 0);
  }

  /**
   * The staff member's specialities in the printout language, in the
   * settings order: the setting label, else the label in the UI language,
   * else the legacy specialities.<code> translation. A code with none of
   * these (e.g. the stray "false" rows of old user saves) is left out — the
   * paper shows what the user form shows.
   */
  private specialityLabels(
    data: VisitCertificatePrintData,
    translate: PrintTranslateFn
  ): Array<string> {
    const codes = (data.specialityCodes || [])
      .map((code) => cleanPrintText(code))
      .filter((code) => code.length > 0);
    const settings = data.specialitySettings || [];
    const settingIndex = (code: string): number => {
      const index = settings.findIndex((setting) => setting.code === code);
      return index < 0 ? settings.length : index;
    };
    const labels = [...codes]
      .sort((a, b) => settingIndex(a) - settingIndex(b))
      .map((code) => {
        const settingLabel =
          cleanPrintText(
            settings.find((item) => item.code === code)?.description
          ) ||
          cleanPrintText(
            (data.fallbackSpecialitySettings || []).find(
              (item) => item.code === code
            )?.description
          );
        if (settingLabel) {
          return settingLabel;
        }
        const key = `specialities.${code}`;
        const translated = translate(key);
        return translated !== key ? cleanPrintText(translated) : '';
      })
      .filter((label) => label.length > 0);
    return labels.filter((label, index) => labels.indexOf(label) === index);
  }

  /** A4 portrait in points. Separate so a test can watch the drawing calls
   *  (jsPDF defines them per instance, not on the prototype). */
  private createDocument(): jsPDF {
    return new jsPDF({ unit: 'pt', format: 'a4' });
  }

  private setFont(
    doc: jsPDF,
    style: 'normal' | 'bold',
    size: number,
    color: Rgb
  ): void {
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
    doc.setTextColor(color[0], color[1], color[2]);
  }

  private drawRule(
    doc: jsPDF,
    fromX: number,
    toX: number,
    y: number,
    color: Rgb,
    width: number
  ): void {
    doc.setDrawColor(color[0], color[1], color[2]);
    doc.setLineWidth(width);
    doc.line(fromX, y, toX, y);
  }

  /** Wraps the text to the width in the current font, at most maxLines
   *  lines — the last kept line ends with "…" when text was cut, so the
   *  certificate always stays on one page. */
  private splitLines(
    doc: jsPDF,
    text: string,
    width: number,
    maxLines: number
  ): Array<string> {
    const lines = doc.splitTextToSize(text, width) as Array<string>;
    if (lines.length <= maxLines) {
      return lines;
    }
    const kept = lines.slice(0, maxLines);
    let last = `${kept[maxLines - 1].trimEnd()}\u2026`;
    while (last.length > 1 && doc.getTextWidth(last) > width) {
      last = `${last.slice(0, -2).trimEnd()}\u2026`;
    }
    kept[maxLines - 1] = last;
    return kept;
  }

  private formatDay(value: Date | string | null | undefined): string {
    if (!value) {
      return '';
    }
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? '' : formatDate(date, 'dd.MM.yyyy', 'fi-FI');
  }

  buildFileName(customerName: string | null | undefined): string {
    const safeName =
      (customerName || '')
        .normalize('NFKD')
        .replace(/[\u0300-\u036F]/g, '')
        .replace(/[^0-9a-zA-Z\-_]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
        .toLowerCase() || 'asiakas';
    const dateSuffix = formatDate(new Date(), 'ddMMyy', 'fi-FI');
    return `${safeName}_kayntitodistus_${dateSuffix}.pdf`;
  }

  /** The preview tab, opened within the click (see openPdfPreviewWindow). */
  openPreviewWindow(): Window | null {
    return openPdfPreviewWindow();
  }

  /** Shows the PDF in the tab of openPreviewWindow (see showPdfPreview). */
  showPreview(
    previewWindow: Window | null,
    blob: Blob,
    fileName: string
  ): boolean {
    return showPdfPreview(previewWindow, blob, fileName);
  }
}
